import type {
  BrandId,
  CreateInviteCodeRequest,
  CreateInviteCodeResponse,
  CreatePlanFromInviteCodeDiscountsRequest,
  CreatePlanFromInviteCodeDiscountsResponse,
  DisableInviteCodeResponse,
  EnableInviteCodeResponse,
  GetInviteActivationMintDefaultsResponse,
  GetInviteActivationSettingsResponse,
  InviteCodePlanRebindPreviewResponse,
  InviteCodeStatus,
  ListInviteCodeActivationsResponse,
  ListInviteCodeSalespeopleResponse,
  ListInviteCodesResponse,
  UpdateInviteActivationSettingsRequest,
  UpdateInviteActivationSettingsResponse,
  UpdateInviteCodePlanRequest,
  UpdateInviteCodePlanResponse,
  UpdateInviteCodeQuotaRequest,
  UpdateInviteCodeQuotaResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';
import { MODEL_PLANS_QUERY_KEY } from './modelPlans.ts';

/**
 * Typed wrappers + React Query hooks for `/v1/ops/invite-codes`
 * (epic #1475) — the salesperson invite-code workspace data layer.
 *
 * Scope is NOT a client concern: ops-api pins the row scope per session
 * tier (CM → own codes, lead → own sales-profile brand, admin →
 * cross-brand). The filters below are the client-tunable ones only —
 * `status` for every tier, `brand` additionally for admin. A scoped
 * session that sent `brand` would simply have it overwritten downstream,
 * so the page never offers it outside the admin view.
 *
 * The settings singleton is admin-only on the server; its hooks take an
 * `enabled` flag so a lead/CM session never fires a request that would
 * come back 403. Mint prefills go through `/defaults`, which a sales lead
 * may read; that body omits login_code_*.
 */

const BASE_PATH = '/v1/ops/invite-codes';
const SETTINGS_PATH = `${BASE_PATH}/settings`;
const DEFAULTS_PATH = `${BASE_PATH}/defaults`;
const SALESPEOPLE_PATH = `${BASE_PATH}/salespeople`;

export const INVITE_CODES_QUERY_KEY = ['invite-codes'] as const;
export const INVITE_ACTIVATION_SETTINGS_QUERY_KEY = ['invite-activation-settings'] as const;
export const INVITE_ACTIVATION_DEFAULTS_QUERY_KEY = ['invite-activation-defaults'] as const;
export const INVITE_CODE_SALESPEOPLE_QUERY_KEY = ['invite-code-salespeople'] as const;
export const INVITE_CODE_ACTIVATIONS_QUERY_KEY = ['invite-code-activations'] as const;
export const INVITE_CODE_REBIND_PREVIEW_QUERY_KEY = ['invite-code-rebind-preview'] as const;

export interface InviteCodeListFilters {
  readonly status?: InviteCodeStatus | undefined;
  /** Admin only — a scoped session's value is replaced by ops-api. */
  readonly brand?: string | undefined;
}

function buildListPath(filters: InviteCodeListFilters): string {
  const params = new URLSearchParams();
  if (filters.status !== undefined) {
    params.set('status', filters.status);
  }
  if (filters.brand !== undefined) {
    params.set('brand', filters.brand);
  }
  const query = params.toString();
  return query.length === 0 ? BASE_PATH : `${BASE_PATH}?${query}`;
}

export async function listInviteCodes(
  filters: InviteCodeListFilters,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListInviteCodesResponse> {
  return opsApiRequest<ListInviteCodesResponse>(buildListPath(filters), { signal }, fetchImpl);
}

export async function listInviteCodeSalespeople(
  brand: BrandId | undefined,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListInviteCodeSalespeopleResponse> {
  const path =
    brand === undefined
      ? SALESPEOPLE_PATH
      : `${SALESPEOPLE_PATH}?${new URLSearchParams({ brand }).toString()}`;
  return opsApiRequest<ListInviteCodeSalespeopleResponse>(path, { signal }, fetchImpl);
}

/**
 * Customers one code brought in. Scope is the server's business here too:
 * ops-api answers 404 for a code the session may not read, so the caller
 * never has to decide whether it is allowed to ask.
 */
export async function listInviteCodeActivations(
  codeId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListInviteCodeActivationsResponse> {
  return opsApiRequest<ListInviteCodeActivationsResponse>(
    `${BASE_PATH}/${encodeURIComponent(codeId)}/activations`,
    { signal },
    fetchImpl,
  );
}

export async function createInviteCode(
  body: CreateInviteCodeRequest,
  fetchImpl: typeof fetch,
): Promise<CreateInviteCodeResponse> {
  return opsApiRequest<CreateInviteCodeResponse>(
    BASE_PATH,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function disableInviteCode(
  id: string,
  fetchImpl: typeof fetch,
): Promise<DisableInviteCodeResponse> {
  return opsApiRequest<DisableInviteCodeResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/disable`,
    { method: 'POST' },
    fetchImpl,
  );
}

export async function enableInviteCode(
  id: string,
  fetchImpl: typeof fetch,
): Promise<EnableInviteCodeResponse> {
  return opsApiRequest<EnableInviteCodeResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/enable`,
    { method: 'POST' },
    fetchImpl,
  );
}

export async function updateInviteCodeQuota(
  id: string,
  body: UpdateInviteCodeQuotaRequest,
  fetchImpl: typeof fetch,
): Promise<UpdateInviteCodeQuotaResponse> {
  return opsApiRequest<UpdateInviteCodeQuotaResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/quota`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function updateInviteCodePlan(
  id: string,
  body: UpdateInviteCodePlanRequest,
  fetchImpl: typeof fetch,
): Promise<UpdateInviteCodePlanResponse> {
  return opsApiRequest<UpdateInviteCodePlanResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/plan`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function getInviteCodePlanRebindPreview(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<InviteCodePlanRebindPreviewResponse> {
  return opsApiRequest<InviteCodePlanRebindPreviewResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/plan/rebind-preview`,
    { signal },
    fetchImpl,
  );
}

export async function createPlanFromInviteCodeDiscounts(
  id: string,
  body: CreatePlanFromInviteCodeDiscountsRequest,
  fetchImpl: typeof fetch,
): Promise<CreatePlanFromInviteCodeDiscountsResponse> {
  return opsApiRequest<CreatePlanFromInviteCodeDiscountsResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/plan-from-discounts`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function getInviteActivationSettings(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetInviteActivationSettingsResponse> {
  return opsApiRequest<GetInviteActivationSettingsResponse>(
    SETTINGS_PATH,
    { signal },
    fetchImpl,
  );
}

export async function getInviteActivationMintDefaults(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetInviteActivationMintDefaultsResponse> {
  return opsApiRequest<GetInviteActivationMintDefaultsResponse>(
    DEFAULTS_PATH,
    { signal },
    fetchImpl,
  );
}

export async function updateInviteActivationSettings(
  body: UpdateInviteActivationSettingsRequest,
  fetchImpl: typeof fetch,
): Promise<UpdateInviteActivationSettingsResponse> {
  return opsApiRequest<UpdateInviteActivationSettingsResponse>(
    SETTINGS_PATH,
    { method: 'PUT', body },
    fetchImpl,
  );
}

/**
 * `enabled` gates the fetch until the session has resolved — the page
 * cannot know whether it may send an admin brand filter before it knows
 * the session roles.
 */
export function useInviteCodes(
  filters: InviteCodeListFilters,
  enabled = true,
): UseQueryResult<ListInviteCodesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...INVITE_CODES_QUERY_KEY, filters.status ?? 'any', filters.brand ?? 'scoped'],
    queryFn: ({ signal }) => listInviteCodes(filters, fetchImpl, signal),
    enabled,
  });
}

export function useInviteCodeSalespeople(
  brand: BrandId | undefined,
  enabled = true,
): UseQueryResult<ListInviteCodeSalespeopleResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...INVITE_CODE_SALESPEOPLE_QUERY_KEY, brand ?? 'own'],
    queryFn: ({ signal }) => listInviteCodeSalespeople(brand, fetchImpl, signal),
    enabled,
  });
}

/**
 * The activations dialog is mounted only while open, so the fetch is already
 * gated by that. `enabled` exists for a caller that renders it always-mounted.
 */
export function useInviteCodeActivations(
  codeId: string,
  enabled = true,
): UseQueryResult<ListInviteCodeActivationsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...INVITE_CODE_ACTIVATIONS_QUERY_KEY, codeId],
    queryFn: ({ signal }) => listInviteCodeActivations(codeId, fetchImpl, signal),
    enabled,
  });
}

export function useInviteActivationSettings(
  enabled = true,
): UseQueryResult<GetInviteActivationSettingsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: INVITE_ACTIVATION_SETTINGS_QUERY_KEY,
    queryFn: ({ signal }) => getInviteActivationSettings(fetchImpl, signal),
    enabled,
  });
}

export function useInviteActivationMintDefaults(
  enabled = true,
): UseQueryResult<GetInviteActivationMintDefaultsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: INVITE_ACTIVATION_DEFAULTS_QUERY_KEY,
    queryFn: ({ signal }) => getInviteActivationMintDefaults(fetchImpl, signal),
    enabled,
  });
}

export function useCreateInviteCode(): UseMutationResult<
  CreateInviteCodeResponse,
  Error,
  CreateInviteCodeRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createInviteCode(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVITE_CODES_QUERY_KEY });
    },
  });
}

export function useDisableInviteCode(): UseMutationResult<
  DisableInviteCodeResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => disableInviteCode(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVITE_CODES_QUERY_KEY });
    },
  });
}

export function useEnableInviteCode(): UseMutationResult<
  EnableInviteCodeResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => enableInviteCode(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVITE_CODES_QUERY_KEY });
    },
  });
}

export interface UpdateInviteCodeQuotaVariables {
  readonly id: string;
  readonly body: UpdateInviteCodeQuotaRequest;
}

export function useUpdateInviteCodeQuota(): UseMutationResult<
  UpdateInviteCodeQuotaResponse,
  Error,
  UpdateInviteCodeQuotaVariables
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateInviteCodeQuota(id, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVITE_CODES_QUERY_KEY });
    },
  });
}

export function useUpdateInviteActivationSettings(): UseMutationResult<
  UpdateInviteActivationSettingsResponse,
  Error,
  UpdateInviteActivationSettingsRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => updateInviteActivationSettings(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVITE_ACTIVATION_SETTINGS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: INVITE_ACTIVATION_DEFAULTS_QUERY_KEY });
    },
  });
}

export interface UpdateInviteCodePlanVariables {
  readonly id: string;
  readonly body: UpdateInviteCodePlanRequest;
}

export function useUpdateInviteCodePlan(): UseMutationResult<
  UpdateInviteCodePlanResponse,
  Error,
  UpdateInviteCodePlanVariables
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateInviteCodePlan(id, body, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: INVITE_CODES_QUERY_KEY });
      void qc.invalidateQueries({
        queryKey: [...INVITE_CODE_REBIND_PREVIEW_QUERY_KEY, variables.id],
      });
    },
  });
}

export function useInviteCodePlanRebindPreview(
  codeId: string | null,
  enabled = true,
): UseQueryResult<InviteCodePlanRebindPreviewResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...INVITE_CODE_REBIND_PREVIEW_QUERY_KEY, codeId ?? ''],
    queryFn: ({ signal }) => getInviteCodePlanRebindPreview(codeId ?? '', fetchImpl, signal),
    enabled: enabled && codeId !== null && codeId.length > 0,
  });
}

export interface CreatePlanFromInviteCodeDiscountsVariables {
  readonly id: string;
  readonly body: CreatePlanFromInviteCodeDiscountsRequest;
}

export function useCreatePlanFromInviteCodeDiscounts(): UseMutationResult<
  CreatePlanFromInviteCodeDiscountsResponse,
  Error,
  CreatePlanFromInviteCodeDiscountsVariables
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => createPlanFromInviteCodeDiscounts(id, body, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: INVITE_CODES_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: MODEL_PLANS_QUERY_KEY });
      void qc.invalidateQueries({
        queryKey: [...INVITE_CODE_REBIND_PREVIEW_QUERY_KEY, variables.id],
      });
    },
  });
}
